import { useMemo, useRef } from "react";

import { QuestionType } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";
import { v4 } from "uuid";

import { QuestionScope } from "../../types";
import {
  COMMUNICATION_PREFERENCES_TYPE,
  FormValues,
  FUND_DESIGNATION_TYPE,
  QuestionFormKind,
  QUESTION_TYPES_WITH_CHOICES,
  ResponseFormatType,
} from "../types";

import { useTranslate } from "@/common/hooks/useTranslate";

const BASE_TYPES: QuestionType[] = [
  QuestionType.Simple,
  QuestionType.MultipleCheckboxes,
  QuestionType.Multiple,
  QuestionType.Checkbox,
  QuestionType.Email,
  QuestionType.PhoneNumber,
  QuestionType.Date,
];

// A sign-up form always asks for an email and carries a phone toggle, so those two would look duplicated
const NEWSLETTER_TYPES: QuestionType[] = BASE_TYPES.filter(
  (type) => type !== QuestionType.Email && type !== QuestionType.PhoneNumber
);

export const useQuestionTypeConfig = (options: {
  form: UseFormReturn<FormValues>;
  selectedScope: QuestionScope;
  isEditing: boolean;
  showFundDesignation: boolean;
  formHasMoreThanOneFund: boolean;
  formKind: QuestionFormKind;
  showCommunicationPreferences?: boolean;
  canAddAttendeeName?: boolean;
  selectedType?: ResponseFormatType;
}) => {
  const {
    form,
    selectedScope,
    isEditing,
    showFundDesignation,
    formHasMoreThanOneFund,
    formKind,
    showCommunicationPreferences = false,
    canAddAttendeeName = true,
    selectedType,
  } = options;
  const { t } = useTranslate();
  const lastAutoFilledTitleRef = useRef<string | null>(null);

  const getDefaultQuestionTitle = (type: ResponseFormatType, scope: QuestionScope): string | undefined => {
    switch (type) {
      case QuestionType.Email:
        return scope === "participant"
          ? t("dashboard", "email")
          : t("dashboard", "donationForm.questionBodyDefaultValue.Email");
      case QuestionType.PhoneNumber:
        return t("dashboard", "donationForm.questionBodyDefaultValue.PhoneNumber");
      case QuestionType.Name:
        return t("dashboard", "donationForm.questionBodyDefaultValue.FullName");
      case QuestionType.Address:
        return t("dashboard", "stepQuestionsV4.dialog.fullAddress");
      default:
        return undefined;
    }
  };

  const handleTypeChange = (newType: ResponseFormatType) => {
    if (newType === FUND_DESIGNATION_TYPE) {
      form.setValue("choices", []);
      form.setValue("isMaximumActive", false);
      return;
    }

    if (newType === COMMUNICATION_PREFERENCES_TYPE) {
      form.setValue("isMaximumActive", false);
      const currentChoices = form.getValues("choices");
      if (!currentChoices || currentChoices.length === 0) {
        form.setValue("choices", [{ _key: v4(), body: "", sortIndex: 0, maximum: null }]);
      }
      return;
    }

    if (QUESTION_TYPES_WITH_CHOICES.includes(newType)) {
      const currentChoices = form.getValues("choices");
      if (!currentChoices || currentChoices.length === 0) {
        form.setValue("choices", [
          { _key: v4(), body: "", sortIndex: 0, maximum: null },
          { _key: v4(), body: "", sortIndex: 1, maximum: null },
        ]);
      }
    } else {
      form.setValue("choices", []);
      form.setValue("isMaximumActive", false);
    }

    const currentScope = form.getValues("scope");
    const defaultTitle = getDefaultQuestionTitle(newType, currentScope);

    const hasFixedQuestionTitle =
      currentScope === "participant" &&
      (newType === QuestionType.Name || newType === QuestionType.Email || newType === QuestionType.Address);

    if (defaultTitle && (!isEditing || hasFixedQuestionTitle)) {
      form.setValue("question", defaultTitle);
      lastAutoFilledTitleRef.current = defaultTitle;
      return;
    }

    if (!isEditing && form.getValues("question") === lastAutoFilledTitleRef.current) {
      form.setValue("question", "");
      lastAutoFilledTitleRef.current = null;
    }
  };

  const isAutoFilledTitle = (title: string) => title !== "" && title === lastAutoFilledTitleRef.current;

  const selectableTypes = useMemo((): ResponseFormatType[] => {
    const types: ResponseFormatType[] = [...(formKind === "newsletter-form" ? NEWSLETTER_TYPES : BASE_TYPES)];

    // fund designation belongs in second position, right after short text
    if (showFundDesignation && !formHasMoreThanOneFund) {
      types.splice(1, 0, FUND_DESIGNATION_TYPE);
    }

    // Preferences are the contact's own subscriptions, so only the buyer answers them, never a guest.
    if (showCommunicationPreferences && selectedScope !== "participant") {
      types.push(COMMUNICATION_PREFERENCES_TYPE);
    }

    if (selectedScope === "participant" && (canAddAttendeeName || selectedType === QuestionType.Name)) {
      types.unshift(QuestionType.Name);
    }

    return types;
  }, [
    formKind,
    selectedScope,
    showFundDesignation,
    formHasMoreThanOneFund,
    showCommunicationPreferences,
    canAddAttendeeName,
    selectedType,
  ]);

  return { getDefaultQuestionTitle, handleTypeChange, selectableTypes, isAutoFilledTitle };
};
